<template>
  <section
    id="consultation"
    ref="revealTarget"
    class="consultation-section home-scroll-reveal"
    :class="{ 'is-reveal-ready': isRevealReady, 'is-revealed': isRevealed }"
  >
    <div class="consultation-background" aria-hidden="true">
      <img src="/images/home/contact-xingke-bg.jpg" alt="" loading="lazy" />
    </div>
    <div class="consultation-tint" aria-hidden="true"></div>

    <div class="page-container consultation-content">
      <div class="consultation-heading">
        <h2>按需求进入对应咨询路径</h2>
        <p>根据合作、采购或研学需求进入对应咨询路径，我们会尽快与您联系。</p>
      </div>

      <div class="consultation-list" aria-label="咨询服务入口">
        <contact-dialog v-for="item in actions" :key="item.label" :default-type="item.label">
          <button class="consultation-item" type="button">
            <span class="consultation-code">{{ item.code }}</span>
            <span class="consultation-copy">
              <strong>{{ item.label }}</strong>
              <small>{{ item.description }}</small>
            </span>
            <i class="i-custom-enter consultation-arrow" aria-hidden="true"></i>
          </button>
        </contact-dialog>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  defineOptions({
    name: 'HomeConsultation',
  })

  const {
    targetRef: revealTarget,
    isReady: isRevealReady,
    isVisible: isRevealed,
  } = useScrollReveal(0.12)

  const actions = [
    {
      code: '01',
      description: '科研协同、中试验证、成果转化、定制生产',
      label: '合作咨询',
    },
    {
      code: '02',
      description: '产品、渠道、团购与购买方式',
      label: '采购咨询',
    },
    {
      code: '03',
      description: '学校、机构、亲子团队与活动需求',
      label: '研学咨询',
    },
  ]
</script>

<style scoped lang="scss">
  .consultation-section {
    position: relative;
    min-height: 488px;
    overflow: hidden;
    color: var(--white);
    background: #73101f;
    isolation: isolate;
  }

  .consultation-background,
  .consultation-tint {
    position: absolute;
    inset: 0;
  }

  .consultation-background {
    z-index: 0;
  }

  .consultation-background img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
  }

  .consultation-tint {
    z-index: 1;
    pointer-events: none;
    background: linear-gradient(
      90deg,
      rgb(76 3 16 / 84%) 0%,
      rgb(120 12 29 / 77%) 48%,
      rgb(139 24 38 / 68%) 100%
    );
  }

  .consultation-content {
    position: relative;
    z-index: 2;
    min-height: 488px;
    padding-block: 116px 112px;
  }

  .consultation-heading {
    display: grid;
    grid-template-columns: minmax(0, 1.22fr) minmax(320px, 0.78fr);
    gap: 72px;
    align-items: start;
  }

  .consultation-heading h2 {
    margin: 0;
    font-size: clamp(30px, 3vw, 42px);
    font-weight: 620;
    line-height: 1.25;
    color: #fff;
  }

  .consultation-heading p {
    max-width: 36em;
    margin: 0;
    font-size: 16px;
    line-height: 1.75;
    color: rgb(255 255 255 / 82%);
  }

  .consultation-list {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin-top: 54px;
    background: rgb(68 0 14 / 24%);
    border-top: 1px solid rgb(255 255 255 / 26%);
    border-bottom: 1px solid rgb(255 255 255 / 26%);
  }

  .consultation-list :deep(.contact-trigger) {
    display: block;
    width: 100%;
  }

  .consultation-list :deep(.contact-trigger + *) {
    width: 100%;
  }

  .consultation-item {
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr) 18px;
    gap: 16px;
    align-items: start;
    width: 100%;
    min-height: 150px;
    padding: 28px 30px;
    color: var(--white);
    text-align: left;
    cursor: pointer;
    background: transparent;
    border: 0;
    border-right: 1px solid rgb(255 255 255 / 24%);
    transition: background var(--fast) var(--ease);
  }

  .consultation-list > .consultation-item:last-of-type,
  .consultation-list > :last-child .consultation-item {
    border-right: 0;
  }

  .consultation-item:hover {
    background: rgb(255 255 255 / 9%);
  }

  .consultation-code {
    padding-top: 2px;
    font: 600 14px/1.4 var(--num);
    color: rgb(255 255 255 / 88%);
  }

  .consultation-copy {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .consultation-copy strong {
    font-size: 18px;
    font-weight: 620;
    line-height: 1.4;
  }

  .consultation-copy small {
    margin-top: 10px;
    font-size: 13px;
    line-height: 1.6;
    color: rgb(255 255 255 / 76%);
  }

  .consultation-arrow {
    width: 18px;
    height: 18px;
    margin-top: 2px;
    transition: transform var(--fast) var(--ease);
  }

  .consultation-item:hover .consultation-arrow {
    transform: translateX(4px);
  }

  @media (max-width: 767px) {
    .consultation-section,
    .consultation-content {
      min-height: 700px;
    }

    .consultation-background img {
      object-position: center center;
    }

    .consultation-tint {
      background: rgb(91 4 20 / 82%);
    }

    .consultation-content {
      padding-block: 64px;
    }

    .consultation-heading {
      grid-template-columns: 1fr;
      gap: 20px;
    }

    .consultation-heading h2 {
      max-width: 10em;
      font-size: 32px;
    }

    .consultation-heading p {
      font-size: 14px;
      line-height: 1.7;
    }

    .consultation-list {
      grid-template-columns: 1fr;
      margin-top: 36px;
    }

    .consultation-item {
      grid-template-columns: 36px minmax(0, 1fr) 18px;
      gap: 12px;
      min-height: 112px;
      padding: 22px 16px;
      border-right: 0;
      border-bottom: 1px solid rgb(255 255 255 / 22%);
    }

    .consultation-list > .consultation-item:last-of-type,
    .consultation-list > :last-child .consultation-item {
      border-bottom: 0;
    }

    .consultation-copy strong {
      font-size: 17px;
    }

    .consultation-copy small {
      margin-top: 7px;
      font-size: 12px;
    }
  }
</style>
